import { useEffect, useState } from "react";

import ApplicationBlock from "./ApplicationBlock";

import { useAeApp } from "@hooks/useAeApp";
import { useBusApp } from "@hooks/useBusApp";
import { useEcheckinApp } from "@hooks/useEcheckinApp";
import { useOeApp } from "@hooks/useOeApp";
import { useEprApp } from "@hooks/useEprApp";

import type { Student } from "@/types/GlobalState";

interface StudentApplicationsProps {
    expand: boolean;
    isGuardian: boolean;
    lang: string;
    loader: boolean;
    setExpand: (val: boolean) => void;
    setLoader?: (val: boolean) => void;
    student: Student;
}

/**
 * Display all relevant applications for the selected student
 * @name StudentApplications
 */
const StudentApplications = ({
    expand,
    isGuardian,
    lang,
    loader,
    setExpand,
    setLoader,
    student
}: StudentApplicationsProps): JSX.Element => {
    const { appObj: echeckinApp, loading: echeckinLoading } = useEcheckinApp(student);
    const { appObj: eprApp, loading: eprLoading } = useEprApp({ student });
    const { appObj: aeApp, loading: aeLoading } = useAeApp(student);
    const { appObj: busApp, loading: busLoading } = useBusApp(isGuardian, student);
    const { appObj: oeApp, loading: oeLoading } = useOeApp(student);

    const [noApps, setNoApps] = useState(false);

    const studentKey = student?.key ?? String(student?.studentNumber ?? "");

    /**
     * If all apps are finished loading, remove the page loader
     */
    useEffect(() => {
        if (!aeLoading && !busLoading && !echeckinLoading && !eprLoading && !oeLoading) {
            setLoader?.(false);
        } else {
            setLoader?.(true);
        }
    }, [aeLoading, busLoading, echeckinLoading, eprLoading, oeLoading, setLoader]);

    /**
     * If there are no apps to display, display a message
     */
    useEffect(() => {
        if ((oeApp || echeckinApp || eprApp || aeApp || busApp) && !loader) {
            setNoApps(false);
        } else {
            setNoApps(true);
        }
    }, [aeApp, busApp, echeckinApp, eprApp, loader, oeApp]);

    if (noApps && !loader) {
        const noApp = {
            app: "NOAPP",
            body: "NoApp_Body1",
            buttons: null,
            endDate: null,
            icon: "DCSD",
            moreInfo: {},
            startDate: null,
            status: "NoApp_Closed",
            statusColor: "#0669B3", // pearl blue
            subBody: "NoApp_Body2",
            title: "NoApp_Header"
        };
        return (
            <div>
                <ApplicationBlock application={noApp} lang={lang} />
            </div>
        );
    }

    return (
        <div>
            {echeckinApp && !echeckinLoading && (
                <ApplicationBlock application={echeckinApp} key={`app-block-${studentKey}-echeckin`} lang={lang} />
            )}
            {oeApp && !oeLoading && (
                <ApplicationBlock
                    application={oeApp}
                    expand={expand}
                    key={`app-block-${studentKey}-oe`}
                    lang={lang}
                    setExpand={setExpand}
                    student={student}
                />
            )}
            {eprApp && !eprLoading && (
                <ApplicationBlock
                    application={eprApp}
                    key={`app-block-${studentKey}-epr`}
                    lang={lang}
                    setExpand={setExpand}
                />
            )}
            {aeApp && !aeLoading && (
                <ApplicationBlock application={aeApp} key={`app-block-${studentKey}-ae`} lang={lang} />
            )}
            {busApp && !busLoading && (
                <ApplicationBlock application={busApp} key={`app-block-${studentKey}-bus`} lang={lang} />
            )}
        </div>
    );
};

export default StudentApplications;
